var _ = require('underscore');
var TorsoListView = require('backbone-torso/modules/ListView');
var estimatedBillsTemplate = require('./estimatedBillsTemplate.hbs');
var SecureMessageAccountEstimatedBillView = require('./SecureMessageAccountEstimatedBillView');
var SecureMessageDataBehavior = require('./../secure-message/SecureMessageDataBehavior');
var InsuranceEstimationDataBehavior = require('../estimate/InsuranceEstimationDataBehavior');
var financialFeatureConfigModel = require('./../financial/financialFeatureConfigModel');

/**
 * Widget to show the estimated bills list in e-reg or pre-reg.
 * @class EstimatedBillsWidget
 * @constructor
 * @extends TorsoListView
 */
module.exports = TorsoListView.extend({
  tagName: 'ol',
  className: 'brec-list',
  template: estimatedBillsTemplate,
  itemContainer: 'estimated-bills',
  itemView: SecureMessageAccountEstimatedBillView,

  behaviors: {
    appointments: {
      behavior: SecureMessageDataBehavior,
      alwaysFetch: true,
      ids: function(cache) {
        var criteria = {
          isPatient: true,
          appointmentCriteria: {
            externalAppointmentCriteria: {
              externalIds: this.view._appointmentExternalIds
            }
          }
        };
        return cache.fetchIdsByCriteria(criteria);
      }
    },
    insuranceEstimations: {
      behavior: InsuranceEstimationDataBehavior,
      alwaysFetch: true,
      renderOnFetch: true,
      updateEvents: ['behaviors.appointments:fetched', 'view.financialConfigLoaded:change'],
      ids: function() {
        var appointments = this.view.getBehavior('appointments').data.getModels();
        var accountExternalIds = _.map(appointments, function(appointment) {
          return appointment.get('appointmentDTO.accountExternalId');
        });
        var isEstimatedCostEnabled = financialFeatureConfigModel.isEstimatedCostEnabled(financialFeatureConfigModel.CONFIG_LEVEL.NORMAL);
        if (accountExternalIds && isEstimatedCostEnabled) {
          return accountExternalIds;
        } else {
          return { skipObjectRetrieval: true };
        }
      }
    }
  },

  /**
   * @method initialize
   * @override
   * @param options {Object}
   *   @param options.appointmentExternalIds {List} the external id of the appointments to get the estimate (via account) for.
   *   @param options.visitId {String} the id of the visit to use to get the insurances for this appointment.
   */
  initialize: function(options) {
    options = options || {};
    this._appointmentExternalIds = options.appointmentExternalIds;
    var insuranceEstimationsBehavior = this.getBehavior('insuranceEstimations');
    this.collection = insuranceEstimationsBehavior.data.privateCollection;
    this.listenTo(insuranceEstimationsBehavior, 'fetched', function() {
      this.trigger('change:totalEstimatedBillsAmount');
    });

    financialFeatureConfigModel.loadOnce()
        .then(this._setFinancialConfigLoaded);
  },

  /**
   * Marks that the financial feature config has finished loading
   * @method setFinancialConfigLoaded
   * @private
   */
  _setFinancialConfigLoaded: function() {
    this.set('financialConfigLoaded', true);
  },

  /**
   * @method getEstimatedBillsCount
   * @returns {Number} the number of estimated bills fetched from the server.  returns 0 if none have been fetched.
   */
  getEstimatedBillsCount: function () {
    var estimatedBillsCount = this.collection && this.collection.length;
    return estimatedBillsCount || 0;
  },

  /**
   * @method allEstimatedBillsWithUnapprovedUpdates
   * @returns {boolean} true if all the estimated bills are having unapproved updates
   */
  allEstimatedBillsWithUnapprovedUpdates: function() {
    var totalCount = this.getEstimatedBillsCount();
    var estimatedBillsWithUnapprovedUpdatesCount = this._getEstimatedBillsWithUnapprovedUpdatesCount();
    var allEstimatedBillsHaveUnapprovedUpdates = totalCount === estimatedBillsWithUnapprovedUpdatesCount;
    return this.hasEstimatedBillsWithUnapprovedUpdates() && allEstimatedBillsHaveUnapprovedUpdates;
  },

  /**
   * @method hasEstimatedBillsWithUnapprovedUpdates
   * @returns {boolean} true if there are any estimated bills with unapproved updates
   */
  hasEstimatedBillsWithUnapprovedUpdates: function() {
    return this._getEstimatedBillsWithUnapprovedUpdatesCount() > 0;
  },

  /**
   * @method _getEstimatedBillsWithUnapprovedUpdatesCount
   * @returns {Number} the number of estimated bills with unapproved updates
   * @private
   */
  _getEstimatedBillsWithUnapprovedUpdatesCount: function() {
    var estimatedBillWithUnapprovedUpdates = _.filter(this.getItemViews(), function(itemView) {
      return itemView.hasUnapprovedUpdates();
    });
    var countWithUnapprovedUpdates = estimatedBillWithUnapprovedUpdates && estimatedBillWithUnapprovedUpdates.length;
    return countWithUnapprovedUpdates || 0;
  },

  /**
   * @method hasEstimates
   * @returns {Boolean} true if there are any estimated bills to display.
   */
  hasEstimates: function() {
    return this.getEstimatedBillsCount() > 0;
  },

  /**
   * @method getTotalEstimatedBillsAmount
   * @returns {Number} the total amount that the patient has in estimated bills.
   */
  getTotalEstimatedBillsAmount: function() {
    var amountsDue = _.map(this.getItemViews(), function(itemView) {
      if (!itemView.hasUnapprovedUpdates()) {
        return itemView.model.get('remainingBalance');
      } else {
        return 0;
      }
    });
    return _.reduce(amountsDue, function(result, amount) {
      return result + amount;
    }, 0);
  }
});
